import React, { useEffect, useRef, useState } from 'react';

/**
 * PortalCityPicker — three surfaces in one:
 *   1. Selected tags up top (click × to remove).
 *   2. Mapbox-backed search input that resolves any city worldwide.
 *   3. A short row of "quick-pick" chips from the curated catalog.
 *
 * Backed by Mapbox forward geocoding (types=place,locality). Selected cities
 * are stored on the backend as kebab slugs; the human-readable label that
 * Mapbox returned is cached in localStorage so we can render proper
 * "Vancouver, British Columbia, Canada" labels instead of UPPERCASE-SLUGS
 * once the page reloads.
 */
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const LABEL_CACHE_KEY = 'portal:cityLabels';
const COORDS_CACHE_KEY = 'portal:cityCoords';

const DEFAULT_QUICK_SLUGS = [
  'vancouver', 'seattle', 'portland', 'san-francisco', 'los-angeles', 'new-york',
  'london', 'paris', 'berlin', 'lisbon', 'mexico-city', 'tokyo',
];

function loadLabelCache() {
  try { return JSON.parse(localStorage.getItem(LABEL_CACHE_KEY) || '{}'); } catch { return {}; }
}
function saveLabelCache(cache) {
  try { localStorage.setItem(LABEL_CACHE_KEY, JSON.stringify(cache)); } catch {}
}
function loadCoordsCache() {
  try { return JSON.parse(localStorage.getItem(COORDS_CACHE_KEY) || '{}'); } catch { return {}; }
}
function saveCoordsCache(cache) {
  try { localStorage.setItem(COORDS_CACHE_KEY, JSON.stringify(cache)); } catch {}
}

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function titleCaseSlug(slug) {
  return (slug || '').split('-').filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function distanceKm(aLat, aLng, bLat, bLng) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export default function PortalCityPicker({
  selected,
  onChange,
  allCities = [],
  quickSelectSlugs = DEFAULT_QUICK_SLUGS,
  placeholder = 'Search any city…',
  /** Optional callback fired with {lat, lng, label} when a Mapbox-picked city is added.
   *  Used by the signal-creation flow to bind primary-city coords. */
  onPickCoords,
  /** When true, only one city may be selected — picking a new one replaces it. */
  single = false,
  /** {lat, lng, label} of the user's location. When set, quick-pick is ordered
   *  by proximity (their detected city first, then nearest cities, then the rest). */
  nearLocation = null,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false); // reveal the search again after a city is picked
  const [labels, setLabels] = useState(loadLabelCache());
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const labelFor = (slug) =>
    labels[slug]
    || allCities.find((c) => c.slug === slug)?.label
    || titleCaseSlug(slug)
    || slug;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q || !MAPBOX_TOKEN) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json`
          + `?types=place,locality&limit=6&language=en&access_token=${MAPBOX_TOKEN}`;
        const r = await fetch(url);
        const data = await r.json();
        const items = (data.features || []).map((f) => {
          const text = f.text || '';
          const ctx = f.context || [];
          const region = ctx.find((c) => (c.id || '').startsWith('region.'))?.text;
          const country = ctx.find((c) => (c.id || '').startsWith('country.'))?.text;
          const label = [text, region, country].filter(Boolean).join(', ');
          const slug = slugify([text, region || country].filter(Boolean).join('-'));
          const [lng, lat] = f.center || [];
          return { slug, label, lat, lng };
        }).filter((r) => r.slug);
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const addCity = ({ slug, label, lat, lng }) => {
    if (!slug || selected.includes(slug)) return;
    const nextLabels = { ...labels, [slug]: label };
    setLabels(nextLabels);
    saveLabelCache(nextLabels);
    if (typeof lat === 'number' && typeof lng === 'number') {
      const coords = loadCoordsCache();
      coords[slug] = { lat, lng };
      saveCoordsCache(coords);
      onPickCoords?.({ slug, label, lat, lng });
    }
    onChange(single ? [slug] : [...selected, slug]);
    setQuery('');
    setResults([]);
    setOpen(false);
    setAdding(false);
    inputRef.current?.blur();
  };

  const removeCity = (slug) => onChange(selected.filter((s) => s !== slug));

  // The user's detected city, pinned first (even if it's not in the catalog).
  const detectedChip = nearLocation?.label
    ? { slug: slugify(nearLocation.label.split(',')[0] + (nearLocation.label.split(',')[1] ? '-' + nearLocation.label.split(',')[1].trim() : '')), label: nearLocation.label, lat: nearLocation.lat, lng: nearLocation.lng, _detected: true }
    : null;

  let quickSelectCities;
  if (nearLocation?.lat != null && nearLocation?.lng != null) {
    // All catalog cities, nearest-first (so: detected city, 3 close ones, then the rest).
    quickSelectCities = allCities
      .filter((c) => !selected.includes(c.slug))
      .filter((c) => !detectedChip || c.slug !== detectedChip.slug)
      .map((c) => ({
        ...c,
        _d: (typeof c.lat === 'number' && typeof c.lng === 'number')
          ? distanceKm(nearLocation.lat, nearLocation.lng, c.lat, c.lng)
          : Number.POSITIVE_INFINITY,
      }))
      .sort((a, b) => a._d - b._d);
  } else {
    quickSelectCities = quickSelectSlugs
      .map((s) => allCities.find((c) => c.slug === s))
      .filter(Boolean)
      .filter((c) => !selected.includes(c.slug));
  }

  // Detected chip leads the list when it isn't already selected.
  const quickPickList = detectedChip && !selected.includes(detectedChip.slug)
    ? [detectedChip, ...quickSelectCities]
    : quickSelectCities;

  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {selected.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => removeCity(s)}
              className="portal-chip active"
              title="Remove"
            >
              {labelFor(s).toUpperCase()} ×
            </button>
          ))}
        </div>
      )}

      {/* Once a city is picked, collapse the search + quick-pick. Tapping "add
          another city" (or removing all via the × chips) brings them back. */}
      {selected.length > 0 && !adding ? (
        <button
          type="button"
          onClick={() => { setAdding(true); setTimeout(() => inputRef.current?.focus(), 0); }}
          className="portal-mono"
          style={{ background: 'none', border: '1px solid var(--line-gold)', color: 'var(--gold)', fontSize: 11, fontWeight: 600, marginTop: 12, padding: '10px 16px', cursor: 'pointer', letterSpacing: '0.18em', textTransform: 'uppercase' }}
        >
          {single ? 'CHANGE CITY →' : '+ ADD ANOTHER CITY'}
        </button>
      ) : (
        <>
          <div style={{ position: 'relative', marginBottom: 12, marginTop: selected.length > 0 ? 12 : 0 }}>
            <input
              ref={inputRef}
              className="portal-input"
              placeholder={placeholder}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
            />
            {open && (results.length > 0 || searching || query.trim()) && (
              <div className="portal-card" style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                padding: 0, maxHeight: 260, overflowY: 'auto', zIndex: 5,
              }}>
                {searching && results.length === 0 ? (
                  <p className="portal-mono" style={{ padding: 12, fontSize: 11, color: 'rgba(236,231,218,0.34)' }}>SEARCHING…</p>
                ) : results.length === 0 ? (
                  <p className="portal-mono" style={{ padding: 12, fontSize: 11, color: 'rgba(236,231,218,0.34)' }}>NO RESULTS</p>
                ) : (
                  results.map((r) => (
                    <button
                      key={r.slug}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => addCity(r)}
                      className="portal-mono"
                      style={{
                        display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left',
                        background: 'transparent', border: 'none', borderBottom: '1px solid rgba(236,231,218,0.12)',
                        color: '#ECE7DA', fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em',
                      }}
                    >
                      {r.label.toUpperCase()}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {quickPickList.length > 0 && (
            <>
              <p className="portal-whisper" style={{ marginBottom: 8 }}>{nearLocation?.lat != null ? 'Nearest cities:' : 'Or quick-pick:'}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 188, overflowY: 'auto' }}>
                {quickPickList.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => addCity({ slug: c.slug, label: c.label, lat: c.lat, lng: c.lng })}
                    className="portal-chip"
                  >
                    {(c.label || '').split(',')[0].toUpperCase()}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// Exported so other Portal Noir surfaces (e.g. Signal wall filter) can render
// the same cached labels for slugs the user has touched before.
export function getCachedCityLabel(slug, allCities = []) {
  if (!slug) return '';
  const cache = loadLabelCache();
  return cache[slug]
    || allCities.find((c) => c.slug === slug)?.label
    || titleCaseSlug(slug)
    || slug;
}

/** Lookup cached coords for a previously-picked city slug. Returns null if missing. */
export function getCachedCityCoords(slug) {
  if (!slug) return null;
  const cache = loadCoordsCache();
  return cache[slug] || null;
}

/** Geocode a city label via Mapbox on-demand. Used as fallback when the
 *  user picks a quick-select chip (no coords cached yet). */
export async function geocodeCityLabel(label) {
  if (!label || !MAPBOX_TOKEN) return null;
  try {
    const r = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(label)}.json`
      + `?types=place,locality&limit=1&language=en&access_token=${MAPBOX_TOKEN}`,
    );
    const data = await r.json();
    const f = (data.features || [])[0];
    if (!f || !f.center) return null;
    const [lng, lat] = f.center;
    return { lat, lng, label: f.place_name || label };
  } catch {
    return null;
  }
}
